import { useTheme } from '@material-ui/core'
import classNames from 'classnames'
import _, { isEqual, uniqBy } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import DataGrid, {
  DataGridHandle,
  FillEvent,
  PasteEvent,
  SortColumn,
} from 'react-data-grid'
import { useTranslation } from 'react-i18next'
import { KeywordDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDataGridStyles } from '../../../components/ProjectPage/BillOfMaterials/AttachmentManager/useAppDataGridStyles'
import { BomItemUpdateGridModel } from './BomItemUpdateGridModel'
import { useBomItemGridColumns } from './useBomItemGridColumns'

type Comparator = (
  a: BomItemUpdateGridModel,
  b: BomItemUpdateGridModel
) => number

function getComparator(sortColumn: string, language: string): Comparator {
  switch (sortColumn) {
    case 'name':
    case 'originalFileName':
      return (a, b) =>
        a.name.localeCompare(b.name, language, {
          numeric: true,
          sensitivity: 'base',
        })
    case 'quantity':
    case 'rowNumber':
      return (a, b) => a[sortColumn] - b[sortColumn]
    case 'keywords':
      return (a, b) =>
        Object.values(a[sortColumn])
          .flat()
          ?.join(',')
          ?.localeCompare(
            Object.values(b[sortColumn]).flat()?.join(',') ?? '',
            language,
            {
              numeric: true,
              sensitivity: 'base',
            }
          ) ?? 0
    default:
      throw new Error(`Unknown sort column: ${sortColumn}`)
  }
}

function handleFill({
  columnKey,
  sourceRow,
  targetRow,
}: FillEvent<BomItemUpdateGridModel>): BomItemUpdateGridModel {
  const allowedColumns = ['quantity', 'keywords', 'comment']

  if (allowedColumns.includes(columnKey)) {
    switch (columnKey) {
      case 'keywords': {
        return mergeQualityAndUserInputedKeywords(sourceRow, targetRow)
      }
      case 'quantity': {
        if (targetRow.enableChangeQuantity) {
          return {
            ...targetRow,
            quantity: sourceRow['quantity'],
          }
        } else {
          return targetRow
        }
      }
      default: {
        return {
          ...targetRow,
          [columnKey]: sourceRow[columnKey],
        }
      }
    }
  }

  return targetRow
}

function mergeQualityAndUserInputedKeywords(
  sourceRow: BomItemUpdateGridModel,
  targetRow: BomItemUpdateGridModel
) {
  const sourceQualitykeywords = sourceRow.keywords['quality'] || []
  const newTargetRowkeywords = {
    ...targetRow.keywords,
    quality: uniqBy(
      [...(targetRow.keywords['quality'] || []), ...sourceQualitykeywords],
      (x) => x.originalKeyword
    ),
    userInputedKeywords: uniqBy(
      [
        ...(targetRow.keywords['userInputedKeywords'] || []),
        ...(sourceRow.keywords['userInputedKeywords'] || []),
      ],
      (x) => x.originalKeyword
    ),
  }

  return {
    ...targetRow,
    keywords: newTargetRowkeywords,
  }
}

/**
 * the paste event triggered by react-data-grid. Needs to comunicate to the document paste event that the pasting was handled by this method.
 * @param e react-data-grid event
 * @param onPasteHandled callback to call when the paste event is handled
 * @returns the modified row
 */
function handlePaste(
  e: PasteEvent<BomItemUpdateGridModel>,
  onPasteHandled: () => void
): BomItemUpdateGridModel {
  //
  // paste only between same columns
  if (e.sourceColumnKey === e.targetColumnKey) {
    onPasteHandled()
    switch (e.targetColumnKey) {
      case 'keywords': {
        // paste only the quality
        return mergeQualityAndUserInputedKeywords(e.sourceRow, e.targetRow)
      }
      default: {
        return {
          ...e.targetRow,
          [e.targetColumnKey]: e.sourceRow[e.sourceColumnKey],
        }
      }
    }
  }

  return e.targetRow
}

function NewFileColumnParser(
  columnKey: keyof BomItemUpdateGridModel,
  value: string
) {
  switch (columnKey) {
    case 'quantity':
      return parseInt(value, 10) || 1
    case 'keywords':
      return {
        userInputedKeywords: value.split(',').map((token) => ({
          originalKeyword: token,
          category: 'userInputedKeywords',
        })),
      }
    case 'comment':
      return value.substring(0, 140)
    default:
      return value
  }
}

const pasteColumns = (
  selectedCellElement,
  sortedRows,
  clipboardRows,
  rowsToUpdate
) => {
  // paste just the columns

  if (!selectedCellElement) return

  const selectedColumnIndex = Number(selectedCellElement.ariaColIndex) - 1
  const selectedRowIndex =
    Number(selectedCellElement.parentElement.ariaRowIndex) - 1
  let clipboarRowCounter = 0

  sortedRows.forEach((file, index) => {
    if (index < Number(selectedRowIndex) - 1) {
      rowsToUpdate.push(file)
      return
    }

    if (index > Number(selectedRowIndex) + clipboardRows.length - 1) {
      rowsToUpdate.push(file)
      return
    }

    const newFile = { ...file }
    const keys = Object.keys(newFile)

    const clipboardRow = clipboardRows[clipboarRowCounter]

    clipboardRow?.forEach((cell, cellIndex) => {
      const currentKey = keys[
        selectedColumnIndex + cellIndex
      ] as keyof BomItemUpdateGridModel

      switch (currentKey) {
        case 'keywords': {
          newFile.keywords = {
            ...file.keywords,
            ...(NewFileColumnParser(currentKey, cell) as Record<
              string,
              KeywordDto[]
            >),
          }
          break
        }
        case 'quantity': {
          if (newFile.enableChangeQuantity) {
            newFile.quantity = NewFileColumnParser('quantity', cell)
          }
          break
        }
        default: {
          newFile[currentKey] = NewFileColumnParser(currentKey, cell) as never
        }
      }
    })

    rowsToUpdate.push(newFile)
    clipboarRowCounter++
  })
}

type BomItemUpdateGridProps = {
  bomItemUpdateModels: BomItemUpdateGridModel[]
  setBomItemUpdateModel: (filesWithDetails: BomItemUpdateGridModel[]) => void
  disabled?: boolean
}

const BomItemBatchUpdateGrid = (props: BomItemUpdateGridProps) => {
  const classes = useAppDataGridStyles()
  const columnsDefinition = useBomItemGridColumns()
  const theme = useTheme()

  const { i18n } = useTranslation()

  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([])
  const gridRef = useRef<DataGridHandle>(null)
  const currentChangedColumn = useRef<number | undefined>(undefined)
  const currentChangedRowId = useRef<number | string | undefined>(undefined)

  const pasteHandled = useRef(false)

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) {
      return props.bomItemUpdateModels
    }

    const sortedRows = [...props.bomItemUpdateModels]

    sortedRows.sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey, i18n.language)
        const compResult = comparator(a, b)

        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult
        }
      }
      return 0
    })

    if (currentChangedRowId.current !== undefined) {
      const currentChangedRowIndex = sortedRows.findIndex(
        (row) => row.id === currentChangedRowId.current
      )

      gridRef.current.scrollToRow(currentChangedRowIndex)
      gridRef.current.selectCell(
        { idx: currentChangedColumn.current, rowIdx: currentChangedRowIndex },
        true
      )
      currentChangedRowId.current = undefined
      currentChangedColumn.current = undefined
    }

    return sortedRows
  }, [props.bomItemUpdateModels, sortColumns, i18n.language])

  const parsePaste = (str) =>
    str.split(/\r\n|\n|\r/).map((row) => row.split('\t'))

  useEffect(() => {
    const handleGlobalPaste = (e: ClipboardEvent) => {
      // should be able to paste a column or multiple columns from the current selected cells (without any ordering)
      // should be able to paste just a single cell from Excel to selected cell
      // if the first column of the pasted data is a string, should paste it respecting the row name

      if (pasteHandled.current) {
        pasteHandled.current = false
        return
      }

      const clipboardRows = parsePaste(e.clipboardData.getData('text/plain'))
      let rowsToUpdate: BomItemUpdateGridModel[] = []

      if (!clipboardRows.length) {
        return
      }

      const selectedCellElement = gridRef.current.element.querySelector(
        '[role="gridcell"][aria-selected="true"]'
      )

      if (
        [3, 4].includes(Number(selectedCellElement.ariaColIndex)) && //the column "name" or "fileName" is selected
        typeof clipboardRows[0][0] === 'string' && // and first column of first pasted data is a string
        props.bomItemUpdateModels.some(
          (row) =>
            row.name?.toLowerCase() === clipboardRows[0][0].toLowerCase() ||
            row.originalFileName?.toLowerCase() ===
              clipboardRows[0][0].toLowerCase()
        ) // and there is a row with the same name or same originalFileName
      ) {
        // paste from respecting the row filename
        clipboardRows.forEach((cells) => {
          const rowToUpdate = props.bomItemUpdateModels.find(
            (x) =>
              x.name?.toLowerCase() === cells[0].toLowerCase() ||
              x.originalFileName?.toLowerCase() === cells[0].toLowerCase()
          )

          if (!rowToUpdate) {
            return
          }

          // assuming that the first column is the name, second is quantity, third is keywords, fourth is comment
          const newRowToUpdate = _.merge({}, rowToUpdate, {
            quantity: rowToUpdate.enableChangeQuantity
              ? Number(cells[1]) || 1
              : rowToUpdate.quantity,
            keywords: cells[2]
              ? {
                  ...rowToUpdate.keywords,
                  userInputedKeywords: cells[2]?.split(',').map((token) => ({
                    originalKeyword: token.trim(),
                    category: 'userInputedKeywords',
                  })),
                }
              : rowToUpdate.keywords,
            comment: cells[3]?.substring(0, 140),
          })

          rowsToUpdate.push(newRowToUpdate)
        })

        //include other rows
        rowsToUpdate = sortedRows.map((originalRow) =>
          Object.assign(
            originalRow,
            rowsToUpdate.find((x) => x.id === originalRow.id)
          )
        )
      } else {
        pasteColumns(
          selectedCellElement,
          sortedRows,
          clipboardRows,
          rowsToUpdate
        )
      }

      if (rowsToUpdate.length) {
        props.setBomItemUpdateModel(rowsToUpdate)
        navigator.clipboard?.writeText('')
      }
    }

    if (!props.disabled) {
      document.addEventListener('paste', handleGlobalPaste)
    } else {
      document.removeEventListener('paste', handleGlobalPaste)
    }

    return () => {
      document.removeEventListener('paste', handleGlobalPaste)
    }
  }, [props, sortedRows])

  const rowHeight = 58

  return (
    <DataGrid
      className={classNames(classes.root, {
        ['rdg-light']: theme.palette.type === 'light',
        ['rdg-dark']: theme.palette.type === 'dark',
      })}
      columns={columnsDefinition}
      rows={sortedRows}
      style={{
        height: 'calc(100vh - 312px)',
      }}
      ref={gridRef}
      rowHeight={rowHeight}
      rowKeyGetter={(row) => row?.id}
      onRowsChange={(rows, data) => {
        currentChangedRowId.current = rows[data.indexes[0]]?.id
        currentChangedColumn.current = data.column.idx
        props.setBomItemUpdateModel(rows)
      }}
      defaultColumnOptions={{
        sortable: true,
        resizable: true,
      }}
      sortColumns={sortColumns}
      onSortColumnsChange={setSortColumns}
      onPaste={(e) => handlePaste(e, () => (pasteHandled.current = true))}
      onFill={handleFill}
      cellNavigationMode="CHANGE_ROW"
    />
  )
}

export default React.memo(BomItemBatchUpdateGrid, isEqual)
